import { defaultListingType, normaliseListingType } from './listingTypes';

export const gridTypes = [
  { id: 'standard', label: 'Standard' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'list', label: 'List' },
  { id: 'rich-content', label: 'Rich Text Content' },
];

export const gridCategories = [
  { id: 'album', label: 'Album' },
  { id: 'article', label: 'Article' },
  { id: 'list', label: 'List' },
  { id: 'listing', label: 'Listing' },
  { id: 'register', label: 'Register' },
  { id: 'menu', label: 'Menu' },
  { id: 'invite', label: 'Invite' },
  { id: 'catalogue', label: 'Catalogue' },
  { id: 'document', label: 'Document' },
  { id: 'store', label: 'Store' },
  { id: 'booking', label: 'Booking' },
  { id: 'service', label: 'Services' },
  { id: 'profile', label: 'Profile' },
];

export const gridAccessLevels = [
  { id: 'inherit', label: 'Inherit link access' },
  { id: 'public', label: 'Public' },
  { id: 'invite-only', label: 'Invite only' },
  { id: 'signed-in', label: 'Require sign in' },
  { id: 'private', label: 'Private' },
];

export const listColumnTypes = [
  { id: 'text', label: 'Text' },
  { id: 'number', label: 'Number' },
  { id: 'date', label: 'Date' },
  { id: 'amount', label: 'Amount' },
  { id: 'percent', label: 'Percent' },
  { id: 'image', label: 'Image' },
  { id: 'file', label: 'File' },
  { id: 'link', label: 'Link' },
  { id: 'formula', label: 'Formula' },
  { id: 'interaction', label: 'Interaction' },
];

export const formulaPresets = [
  { id: 'sum', label: 'Sum' },
  { id: 'subtract', label: 'Subtract' },
  { id: 'multiply', label: 'Multiply' },
  { id: 'divide', label: 'Divide' },
  { id: 'percentage', label: 'Percentage' },
];

export const amountCurrencySymbols = [
  { id: 'R', label: 'R' },
  { id: '$', label: '$' },
  { id: '€', label: '€' },
  { id: '£', label: '£' },
];

export const interactionTypes = [
  { id: 'text', label: 'Text input' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'select', label: 'Select' },
  { id: 'check', label: 'Check' },
  { id: 'rating', label: 'Rating' },
];

export const normaliseListColumnType = (type) => (
  listColumnTypes.some((columnType) => columnType.id === type) ? type : 'text'
);

export const normaliseFormulaPreset = (preset) => (
  formulaPresets.some((formulaPreset) => formulaPreset.id === preset) ? preset : 'sum'
);

export const normaliseAmountCurrencySymbol = (symbol) => (
  amountCurrencySymbols.some((currencySymbol) => currencySymbol.id === symbol) ? symbol : 'R'
);

export const normaliseInteractionType = (type) => (
  interactionTypes.some((interactionType) => interactionType.id === type) ? type : 'text'
);

export const normaliseInteractionOptions = (options) => (
  Array.isArray(options)
    ? options.map((option) => String(option).trim()).filter(Boolean)
    : String(options || '').split(',').map((option) => option.trim()).filter(Boolean)
);

export const normaliseGridType = (type) => (
  gridTypes.some((gridType) => gridType.id === type) ? type : 'standard'
);

export const normaliseGridAccessLevel = (accessLevel) => (
  gridAccessLevels.some((level) => level.id === accessLevel) ? accessLevel : 'inherit'
);

export const createListColumn = (label = 'Column', type = 'text') => ({
  id: `list-column-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  label,
  type: normaliseListColumnType(type),
  currencySymbol: 'R',
  interaction: null,
  formula: null,
});

export const createListRow = (columns = []) => ({
  id: `list-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  cells: Object.fromEntries(columns.map((column) => [column.id, ''])),
  imageUrl: '',
  tags: [],
  actionLabel: '',
  actionUrl: '',
});

export const normaliseListColumns = (columns = []) => {
  const nextColumns = Array.isArray(columns)
    ? columns
      .map((column, index) => ({
        id: column.id || `list-column-${index + 1}`,
        label: String(column.label || '').trim() || `Column ${index + 1}`,
        type: normaliseListColumnType(column.type),
        currencySymbol: normaliseAmountCurrencySymbol(column.currencySymbol),
        interaction: column.interaction && typeof column.interaction === 'object'
          ? {
            type: normaliseInteractionType(column.interaction.type),
            options: normaliseInteractionOptions(column.interaction.options),
          }
          : null,
        formula: column.formula && typeof column.formula === 'object'
          ? {
            preset: normaliseFormulaPreset(column.formula.preset),
            columnIds: Array.isArray(column.formula.columnIds)
              ? column.formula.columnIds.map((columnId) => String(columnId)).filter(Boolean)
              : [],
          }
          : null,
      }))
      .filter((column) => column.id)
    : [];

  return nextColumns.length ? nextColumns : [
    { id: 'list-column-title', label: 'Title', type: 'text', currencySymbol: 'R', interaction: null, formula: null },
    { id: 'list-column-detail', label: 'Detail', type: 'text', currencySymbol: 'R', interaction: null, formula: null },
  ];
};

export const listCellDisplayValue = (value, type = 'text', currencySymbol = 'R') => {
  if (value && typeof value === 'object') {
    return value.label || value.name || value.downloadUrl || value.url || '';
  }
  const stringValue = String(value ?? '');

  if (type === 'amount') {
    const numericValue = Number(stringValue);
    return Number.isFinite(numericValue) ? `${normaliseAmountCurrencySymbol(currencySymbol)} ${numericValue.toFixed(2)}` : stringValue;
  }

  if (type === 'percent') {
    const numericValue = Number(stringValue);
    if (!Number.isFinite(numericValue)) return stringValue;
    return stringValue.includes('%') ? stringValue : `${numericValue}%`;
  }

  return stringValue;
};

export const numericListCellValue = (row = {}, columnId = '') => {
  const value = row.cells?.[columnId];
  if (value && typeof value === 'object') return Number.NaN;
  const parsedValue = Number(String(value ?? '').replace(/[%\s,]/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : Number.NaN;
};

export const calculateFormulaValue = (row = {}, column = {}) => {
  const formula = column.formula || {};
  const columnIds = Array.isArray(formula.columnIds) ? formula.columnIds : [];
  const values = columnIds.map((columnId) => numericListCellValue(row, columnId));
  const hasInvalidValue = values.some((value) => !Number.isFinite(value));

  if (!columnIds.length || hasInvalidValue) return '';

  switch (normaliseFormulaPreset(formula.preset)) {
    case 'subtract':
      return values.length >= 2 ? values.slice(1).reduce((total, value) => total - value, values[0]) : '';
    case 'multiply':
      return values.reduce((total, value) => total * value, 1);
    case 'divide':
      return values.length >= 2 && values.slice(1).every((value) => value !== 0)
        ? values.slice(1).reduce((total, value) => total / value, values[0])
        : '';
    case 'percentage':
      return values.length >= 2 && values[1] !== 0 ? (values[0] / values[1]) * 100 : '';
    case 'sum':
    default:
      return values.reduce((total, value) => total + value, 0);
  }
};

export const normaliseListRows = (rows = [], columns = []) => (
  Array.isArray(rows)
    ? rows.map((row, index) => ({
      id: row.id || `list-row-${index + 1}`,
      cells: Object.fromEntries(columns.map((column) => [
        column.id,
        row.cells?.[column.id] && typeof row.cells[column.id] === 'object'
          ? {
            name: String(row.cells[column.id].name || ''),
            downloadUrl: String(row.cells[column.id].downloadUrl || ''),
            contentType: String(row.cells[column.id].contentType || ''),
            size: Number(row.cells[column.id].size) || 0,
          }
          : String(row.cells?.[column.id] ?? ''),
      ])),
      imageUrl: String(row.imageUrl || ''),
      tags: Array.isArray(row.tags) ? row.tags.map((tag) => String(tag).trim()).filter(Boolean) : [],
      actionLabel: String(row.actionLabel || ''),
      actionUrl: String(row.actionUrl || ''),
    }))
    : []
);

export const normaliseGridLayout = (layout) => (
  layout === 'grid' || layout === 'masonry' ? layout : 'masonry'
);

export const normaliseGridColumns = (columns) => {
  const parsedColumns = Number(columns);
  if (!Number.isFinite(parsedColumns)) return 3;
  return Math.min(Math.max(Math.round(parsedColumns), 1), 4);
};

export const normaliseGridCategory = (category) => (
  gridCategories.some((gridCategory) => gridCategory.id === category) ? category : 'album'
);

export const normaliseAssetGrid = (grid = {}, index = 0, validAssetIds = null, link = {}) => {
  const listColumns = normaliseListColumns(grid.listColumns);

  return {
    id: grid.id || `grid-${index + 1}`,
    title: grid.title?.trim() || `Grid ${index + 1}`,
    type: normaliseGridType(grid.type),
    accessLevel: normaliseGridAccessLevel(grid.accessLevel),
    category: normaliseGridCategory(grid.category || link.category),
    listingType: normaliseListingType(grid.listingType || link.listingType || defaultListingType),
    layout: normaliseGridLayout(grid.layout || link.layout),
    columns: normaliseGridColumns(grid.columns || link.galleryColumns),
    assetIds: (grid.assetIds || []).filter((assetId) => !validAssetIds || validAssetIds.has(assetId)),
    listTitle: String(grid.listTitle || '').trim(),
    listDescription: String(grid.listDescription || ''),
    listImageUrl: String(grid.listImageUrl || ''),
    listTags: Array.isArray(grid.listTags) ? grid.listTags.map((tag) => String(tag).trim()).filter(Boolean) : [],
    listActionLabel: String(grid.listActionLabel || ''),
    listActionUrl: String(grid.listActionUrl || ''),
    listColumns,
    listRows: normaliseListRows(grid.listRows, listColumns),
    richContentTitle: String(grid.richContentTitle || '').trim(),
    richContentMarkdown: String(grid.richContentMarkdown || ''),
  };
};

export const createAssetGrid = (title = 'Assets', assetIds = [], type = 'standard') => ({
  id: `grid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title,
  type: normaliseGridType(type),
  accessLevel: 'inherit',
  category: 'album',
  listingType: defaultListingType,
  layout: 'masonry',
  columns: 3,
  assetIds,
  listTitle: '',
  listDescription: '',
  listImageUrl: '',
  listTags: [],
  listActionLabel: '',
  listActionUrl: '',
  listColumns: normaliseListColumns(),
  listRows: [],
  richContentTitle: '',
  richContentMarkdown: '',
});

const assetIdSet = (assets = []) => new Set(assets.map((asset) => asset.id).filter(Boolean));

export const normaliseAssetGrids = (link = {}) => {
  const assets = link.assets || [];
  const validAssetIds = assetIdSet(assets);
  const savedGrids = Array.isArray(link.assetGrids) ? link.assetGrids : [];

  if (!savedGrids.length) {
    return [{
      id: 'grid-default',
      title: 'All assets',
      type: 'standard',
      accessLevel: 'inherit',
      category: normaliseGridCategory(link.category),
      listingType: normaliseListingType(link.listingType || defaultListingType),
      layout: normaliseGridLayout(link.layout),
      columns: normaliseGridColumns(link.galleryColumns),
      assetIds: assets.map((asset) => asset.id).filter(Boolean),
    }];
  }

  const grids = savedGrids.map((grid, index) => normaliseAssetGrid(grid, index, validAssetIds, link));
  const assignedIds = new Set(grids.flatMap((grid) => grid.assetIds));
  const unassignedIds = assets
    .map((asset) => asset.id)
    .filter((assetId) => assetId && !assignedIds.has(assetId));

  if (unassignedIds.length) {
    grids[0] = {
      ...grids[0],
      assetIds: [...unassignedIds, ...grids[0].assetIds],
    };
  }

  return grids;
};

export const assetsForGrid = (assets = [], grid) => {
  if (!grid) return assets;
  const gridAssetIds = new Set(grid.assetIds || []);
  return assets.filter((asset) => gridAssetIds.has(asset.id));
};

export const addAssetsToGrid = (link = {}, nextAssets = [], gridId = '') => {
  const nextAssetIds = nextAssets.map((asset) => asset.id).filter(Boolean);
  if (!nextAssetIds.length) return link;

  const validAssetIds = assetIdSet(link.assets || []);
  const grids = Array.isArray(link.assetGrids) && link.assetGrids.length
    ? link.assetGrids.map((grid, index) => normaliseAssetGrid(grid, index, validAssetIds, link))
    : normaliseAssetGrids(link);
  const targetGridId = grids.some((grid) => grid.id === gridId) ? gridId : grids[0]?.id;

  return {
    ...link,
    assetGrids: grids.map((grid) => ({
      ...grid,
      assetIds: grid.id === targetGridId
        ? [...new Set([...nextAssetIds, ...(grid.assetIds || [])])]
        : grid.assetIds || [],
    })),
  };
};

export const addAssetsToAllGrids = addAssetsToGrid;
export const addAssetsToFirstGrid = addAssetsToGrid;

export const removeAssetFromGrids = (link = {}, assetId) => ({
  ...link,
  assetGrids: normaliseAssetGrids(link).map((grid) => ({
    ...grid,
    assetIds: (grid.assetIds || []).filter((currentAssetId) => currentAssetId !== assetId),
  })),
});
